import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { invoicesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
const statusMap = { paid: 'badge-green', pending: 'badge-yellow', partially_paid: 'badge-orange', draft: 'badge-gray', cancelled: 'badge-red', converted: 'badge-blue' };

export default function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoicesAPI.list({ search, type: typeFilter, status: statusFilter, page, limit: 50 });
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [search, typeFilter, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <div><h1>Invoices</h1><p className="subtitle">{total} total invoices</p></div>
        <Link to="/invoices/new" className="btn btn-primary">+ New Invoice</Link>
      </div>

      <div className="card">
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="search-bar">
            <div className="search-input-wrapper" style={{ minWidth: 200 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Search invoice # or customer..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-select" style={{ width: 'auto', minWidth: 160 }} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              <option value="proforma">Proforma</option>
              <option value="gst_invoice">GST Invoice</option>
            </select>
            <select className="form-select" style={{ width: 'auto', minWidth: 160 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner" /></div> : (
            <table>
              <thead>
                <tr><th>Invoice #</th><th>Type</th><th>Customer</th><th>Date</th><th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>Paid</th><th style={{textAlign:'right'}}>Balance</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td><Link to={`/invoices/${inv.id}`} className="td-mono" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>{inv.invoice_number}</Link></td>
                    <td><span className={`badge ${inv.invoice_type === 'gst_invoice' ? 'badge-green' : 'badge-blue'}`}>{inv.invoice_type === 'gst_invoice' ? 'GST Invoice' : 'Proforma'}</span></td>
                    <td><div style={{ fontWeight: 500 }}>{inv.customer_name}</div>{inv.customer_gstin && <div className="text-xs text-secondary font-mono">{inv.customer_gstin}</div>}</td>
                    <td className="text-secondary">{new Date(inv.invoice_date).toLocaleDateString('en-IN')}</td>
                    <td className="td-amount font-bold">{fmt(inv.total_amount)}</td>
                    <td className="td-amount text-success">{fmt(inv.paid_amount)}</td>
                    <td className="td-amount" style={{ color: Number(inv.balance_amount) > 0 ? 'var(--danger)' : 'inherit' }}>{fmt(inv.balance_amount)}</td>
                    <td><span className={`badge ${statusMap[inv.payment_status] || 'badge-gray'}`}>{inv.payment_status?.replace('_',' ')}</span></td>
                    <td><Link to={`/invoices/${inv.id}`} className="btn btn-outline btn-sm">View</Link></td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={9} className="text-center text-secondary" style={{padding:40}}>No invoices found</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
